import React, { useState } from 'react';
import { HelpCircle, CheckCircle2, XCircle, MessageSquareText } from 'lucide-react';
import { StepNavButtons } from './StepNavButtons';
import { MarkdownContent } from '../MarkdownContent';
import { explanationFor, type ModuleStep, type StepResultResponse } from '@/lib/models/module';

interface CheckpointStepRendererProps {
    step: ModuleStep;
    /** `{ text }` / `{ selected_option_id }` previously submitted for this step. */
    savedAnswer?: any;
    onSubmit: (answer: any) => Promise<StepResultResponse>;
    isSubmitting: boolean;
    canGoBack: boolean;
    onGoBack: () => void;
}

export const CheckpointStepRenderer: React.FC<CheckpointStepRendererProps> = ({ step, savedAnswer, onSubmit, isSubmitting, canGoBack, onGoBack }) => {
    const { heading, body, question_text, options, correct_option_id, question_type } = step.content;

    // #293 — an OPEN answer. The candidate writes prose, nothing is graded, and
    // the employer reads it later. Everything below that branches on this is
    // about not claiming a verdict we do not have.
    const isOpenAnswer = question_type === 'text_input' || question_type === 'ai_evaluated';

    // Coming BACK to a step already answered — either mid-module or while
    // reviewing a finished one. Both shapes are restored so the step shows what
    // the candidate actually said rather than an empty form.
    const restoredText = isOpenAnswer && typeof savedAnswer?.text === 'string' ? savedAnswer.text : '';
    const restoredOption = !isOpenAnswer && typeof savedAnswer?.selected_option_id === 'string'
        ? savedAnswer.selected_option_id
        : null;
    const hasRestored = isOpenAnswer ? restoredText.trim().length > 0 : restoredOption !== null;

    const [selectedOption, setSelectedOption] = useState<string | null>(restoredOption);
    const [text, setText] = useState(restoredText);
    const [result, setResult] = useState<StepResultResponse | null>(null);
    // The restored answer is shown settled (banner + Fortsätt), until the
    // candidate chooses to revise it.
    const [isEditing, setIsEditing] = useState(false);

    const trimmed = text.trim();
    const canSubmit = isOpenAnswer ? trimmed.length > 0 : selectedOption !== null;

    // The verdict for a restored multiple choice is re-derived from the content
    // the client already holds — the same rule the server applied — because
    // step_results stores the answer, not the grade. An open answer has no
    // verdict to derive, and must stay undefined rather than fall to `false`.
    const restoredCorrect = restoredOption !== null ? restoredOption === correct_option_id : undefined;
    const effectiveCorrect = result !== null ? result.correct : restoredCorrect;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        const res = await onSubmit(isOpenAnswer ? { text: trimmed } : { selected_option_id: selectedOption });
        setResult(res);
    };

    // Settled: either just answered, or re-opened with a saved answer the
    // candidate has not chosen to revise.
    const showFeedback = result !== null || (hasRestored && !isEditing);

    // An open answer carries no verdict, so `correct` arrives undefined — which
    // is exactly the value a plain falsy check would render as "Inte riktigt
    // rätt". Distinguish "no verdict" from "wrong" explicitly.
    const hasVerdict = effectiveCorrect !== undefined && effectiveCorrect !== null;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    {isOpenAnswer
                        ? <MessageSquareText className="w-5 h-5 text-amber-600" />
                        : <HelpCircle className="w-5 h-5 text-amber-600" />}
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900">{heading || step.step_title}</h2>
                    {body && <div className="mt-1"><MarkdownContent>{body}</MarkdownContent></div>}
                </div>
            </div>

            {/* Question */}
            <MarkdownContent className="[&_p]:m-0 [&_p]:text-lg [&_p]:font-medium [&_p]:text-gray-900">
                {question_text}
            </MarkdownContent>

            {/* Open answer — free text, no options, no grading (#293) */}
            {isOpenAnswer && (
                <div className="space-y-2">
                    <textarea
                        disabled={showFeedback}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Skriv ditt svar här..."
                        rows={6}
                        className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-[#1A4D4F] focus:ring-1 focus:ring-[#1A4D4F] outline-none resize-y text-sm disabled:bg-gray-50 disabled:text-gray-600"
                    />
                    {!showFeedback ? (
                        <p className="text-xs text-gray-500">
                            Ditt svar sparas och läses av arbetsgivaren. Det finns inget rätt eller fel.
                        </p>
                    ) : result === null && (
                        // Re-opened an answered step. The answer is shown as the
                        // record it is, but the candidate can still revise it —
                        // otherwise revisiting is a read-only dead end.
                        <button
                            onClick={() => setIsEditing(true)}
                            className="text-xs font-medium text-[#1A4D4F] underline underline-offset-2 hover:text-[#1A4D4F]/80"
                        >
                            Ändra svar
                        </button>
                    )}
                </div>
            )}

            {/* Options — kept in content when the author switches answer type, so
                flipping back to Flerval does not discard the written options. */}
            {!isOpenAnswer && (
            <div className="space-y-2.5">
                {(options as { option_id: string; text: string }[])?.map((option) => {
                    const isSelected = selectedOption === option.option_id;
                    const isCorrect = showFeedback && option.option_id === correct_option_id;
                    const isWrong = showFeedback && isSelected && !effectiveCorrect;

                    let cls = 'border-gray-200 hover:border-[#1A4D4F]/40 hover:bg-gray-50';
                    if (isCorrect) cls = 'border-green-500 bg-green-50';
                    else if (isWrong) cls = 'border-red-400 bg-red-50';
                    else if (isSelected) cls = 'border-[#1A4D4F] bg-[#A4C3B2]/20 shadow-sm';

                    return (
                        <button
                            key={option.option_id}
                            disabled={showFeedback}
                            onClick={() => setSelectedOption(option.option_id)}
                            className={`group flex w-full items-center gap-3 rounded-xl border-2 p-3.5 text-left transition-all disabled:cursor-default ${cls}`}
                        >
                            {isCorrect ? (
                                <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" />
                            ) : isWrong ? (
                                <XCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
                            ) : (
                                <span
                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                                        isSelected ? 'border-[#1A4D4F] bg-[#1A4D4F]' : 'border-gray-300 group-hover:border-[#1A4D4F]/50'
                                    }`}
                                >
                                    {isSelected && <span className="h-2 w-2 rounded-full bg-white" />}
                                </span>
                            )}
                            <span className="text-sm font-medium text-gray-900">{option.text}</span>
                        </button>
                    );
                })}
            </div>
            )}

            {/* Feedback */}
            {showFeedback && (
                <div className={`p-4 rounded-xl ${!hasVerdict ? 'bg-[#A4C3B2]/15 border-2 border-[#A4C3B2]/50' : effectiveCorrect ? 'bg-green-50 border-2 border-green-200' : 'bg-amber-50 border-2 border-amber-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                        {!hasVerdict
                            ? <CheckCircle2 className="w-5 h-5 text-[#1A4D4F]" />
                            : effectiveCorrect
                                ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                                : <XCircle className="w-5 h-5 text-amber-600" />
                        }
                        <span className="font-semibold text-gray-900">
                            {/* No verdict is not a failure and not a pass — say only
                                what is true: the answer was received. */}
                            {!hasVerdict ? 'Svaret är sparat' : effectiveCorrect ? 'Rätt svar!' : 'Inte riktigt rätt'}
                        </span>
                    </div>
                    {/* #294 — the server already resolves which explanation fits the
                        outcome, so prefer its answer; fall back to resolving locally
                        from the step's own content, which is what preview mode and any
                        older response without the field rely on. */}
                    {(() => {
                        // With no verdict there is no outcome to choose an override
                        // by, so an open answer gets the general explanation only —
                        // resolving locally would hand it explanation_incorrect,
                        // scolding a candidate nobody graded.
                        const text = result?.explanation
                            || (hasVerdict ? explanationFor(step.content, !!effectiveCorrect) : step.content.explanation);
                        if (!text) return null;
                        return <div className="mt-1"><MarkdownContent className="[&_p]:m-0 [&_p]:text-sm [&_p]:text-gray-700">{text}</MarkdownContent></div>;
                    })()}
                </div>
            )}

            {/* Submit / Continue */}
            {!showFeedback ? (
                <StepNavButtons
                    canGoBack={canGoBack}
                    onGoBack={onGoBack}
                    onNext={handleSubmit}
                    nextLabel={isSubmitting ? (isOpenAnswer ? 'Sparar...' : 'Kontrollerar...') : 'Svara'}
                    nextDisabled={!canSubmit || isSubmitting}
                />
            ) : (
                <StepNavButtons
                    canGoBack={canGoBack}
                    onGoBack={onGoBack}
                    onNext={() => onSubmit({ _advance: true })}
                    nextLabel="Fortsätt"
                />
            )}
        </div>
    );
};
